import { noddiAsync, URLKeys } from 'noddi-async';
import {
  ErrorPageAdmin,
  NoddiBasicCard,
  NoddiSearchBar,
  NoddiTablePagination,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { useMemo, useState } from 'react';

import { t, Trans } from '@lingui/macro';
import { debounce } from '@mui/material/utils';
import { useAuthContext } from 'noddi-provider';
import { BOOKING_TYPE, TireHotelContractItem } from 'noddi-types';
import { cn, NoddiButton, noddiColors, NoddiDummyButton, NoddiIcon } from 'noddi-ui-common';
import { customerRoutes } from 'noddi-util';
import { useNavigate } from 'react-router-dom';
import getCommonTranslations from '../../commonTranslations';
import { NoddiCarSvg } from '../../components/Home/CarSvg';
import ContentWrapper from '../../components/Layouts/ContentWrapper';
import { NoddiLoadingScreenWithTrans } from '../../components/NoddiLoadingScreenWithTrans';
import { useBookingActions } from '../BookingFlow/bookingStore';

export const FleetHotel = () => {
  const [selectedContracts, setSelectedContracts] = useState<TireHotelContractItem[]>([]);
  const navigate = useNavigate();
  const { clearStore, addCar, setBookingType } = useBookingActions();

  const { page, setPage, pageSize, setPageSize, search, setSearch } = usePaginationQueryParam();
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setPage(1);
      }, 300),
    [setSearch, setPage]
  );

  const { currentUserGroupId } = useAuthContext();

  const {
    data: contracts,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedTireHotelContracts,
    input: {
      userGroupId: currentUserGroupId as number,
      page,
      pageSize,
      search
    },
    queryConfig: {
      enabled: !!currentUserGroupId
    }
  });

  if (error) {
    return <ErrorPageAdmin apiError={[error]} />;
  }

  const toggleSelection = (id: TireHotelContractItem) => {
    setSelectedContracts((prev) => {
      if (prev.includes(id)) {
        return prev.filter((itemId) => itemId !== id);
      }
      return [...prev, id];
    });
  };

  const prePopulateAndNavigate = ({ isCancel }: { isCancel: boolean }) => {
    clearStore();

    selectedContracts.forEach((contract) => {
      const {
        car: { make, model, licensePlate }
      } = contract;
      addCar({
        make,
        model,
        hasActiveTireHotelContract: true,
        licensePlate
      });
    });

    if (isCancel) {
      setBookingType(BOOKING_TYPE.CANCEL_TIER_HOTEL);
    }

    navigate(customerRoutes.newBookingAddress.getPath());
  };

  return (
    <ContentWrapper title={t`Tire hotel`}>
      <div className='flex flex-col gap-4'>
        <div className={cn('flex items-end justify-end gap-2', selectedContracts.length && 'justify-between')}>
          {!!selectedContracts.length && (
            <div className='hidden sm:block'>
              <NoddiButton
                variant='secondary'
                size='small'
                startIcon='Cross'
                onPress={() => {
                  setSelectedContracts([]);
                }}
              >
                <Trans>Clear all</Trans>
              </NoddiButton>
            </div>
          )}
          <div className='flex gap-2'>
            <NoddiButton
              disabled={!selectedContracts.length}
              variant='secondary'
              onPress={() =>
                prePopulateAndNavigate({
                  isCancel: true
                })
              }
            >
              <Trans>End agreement</Trans> ({selectedContracts.length})
            </NoddiButton>
            <NoddiButton
              disabled={!selectedContracts.length}
              onPress={() => prePopulateAndNavigate({ isCancel: false })}
            >
              <Trans>Order wheel change</Trans> ({selectedContracts.length})
            </NoddiButton>
          </div>
        </div>
        <NoddiSearchBar
          handleChange={(event) => {
            debouncedSetSearch(event.target.value);
          }}
          placeholder={t`Search by license plate`}
        />

        {isPending ? (
          <NoddiLoadingScreenWithTrans />
        ) : (
          <div className='flex flex-col gap-2'>
            {contracts.results.map((contract) => {
              const {
                bookingItemSalesItem,
                canRenewContract,
                car: { make, model, licensePlate }
              } = contract;
              const isSelected = selectedContracts.includes(contract);

              return (
                <NoddiBasicCard
                  key={licensePlate.number}
                  onClick={canRenewContract ? () => toggleSelection(contract) : undefined}
                  to={
                    !canRenewContract && bookingItemSalesItem?.bookingId
                      ? customerRoutes.myBookingDetails.getPath({ id: bookingItemSalesItem.bookingId })
                      : undefined
                  }
                  className={cn(
                    'flex items-center justify-between gap-4 px-3 py-2',
                    isSelected && 'bg-purple text-white'
                  )}
                >
                  <div className='flex items-center gap-4'>
                    <NoddiCarSvg roofColor={isSelected ? noddiColors.darkPurple : undefined} scale={0.5} />
                    <div className='flex flex-col'>
                      <span className='font-semibold'>{`${make} - ${model}`}</span>
                      <span>{licensePlate.number}</span>
                    </div>
                  </div>
                  {canRenewContract ? (
                    <NoddiIcon
                      size='large'
                      name={isSelected ? 'CheckedRectangle' : 'Rectangle'}
                      color={isSelected ? noddiColors.white : noddiColors.darkPurple}
                    />
                  ) : (
                    !!bookingItemSalesItem?.bookingId && (
                      <NoddiDummyButton className='-mb-2 -mr-4' variant='link' endIcon='ArrowRight'>
                        <Trans>See booking</Trans>
                      </NoddiDummyButton>
                    )
                  )}
                </NoddiBasicCard>
              );
            })}
          </div>
        )}

        <NoddiTablePagination
          count={contracts?.count ?? 0}
          page={page}
          setPage={setPage}
          rowsPerPage={pageSize}
          setRowsPerPage={setPageSize}
          translations={getCommonTranslations().table}
        />
      </div>
    </ContentWrapper>
  );
};
